import {
  resumeEngagementDataBuilder,
  type FindResumeEngagementResult,
} from "../components/core/JobComponent/EmployerOutreach/ResumeEngagementDataBuilder";
import { useViewjobOneGraphModel } from "../context/ViewJobDataContext";
import { useEmployerOutreachSource } from "./useEmployerOutreachSource";
import type { ResumeEngagementContextData } from "./useFindResumeEngagement";

/**
 * Hook to process resume engagement data from context or OneGraph model
 * @param jobKey - The job key to filter engagements for
 * @param isViewjobOneGraphActive - Whether the Viewjob OneGraph is active
 * @param isEmployerOutreachActive - Whether the Employer Outreach is active
 * @param isFullScreenVJActive - Whether the Hero VJ test is active
 * @param resumeEngagementContext - Resume engagement data from context (from GraphQL query in AppContentGraphQL)
 * @returns Resume engagement data with loading, error states
 */
export const useResumeEngagement = (
  jobKey: string,
  isViewjobOneGraphActive: boolean,
  isEmployerOutreachActive: boolean,
  isFullScreenVJActive: boolean,
  resumeEngagementContext: ResumeEngagementContextData | undefined,
): Partial<FindResumeEngagementResult> => {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const { outreachSource } = useEmployerOutreachSource();

  if (!(isEmployerOutreachActive || isFullScreenVJActive)) {
    return {
      data: undefined,
      loading: false,
      error: undefined,
    };
  }

  // If outreach source is AUTOSOURCER, return undefined
  // AUTOSOURCER engagements should not show resume engagement data
  if (outreachSource === "AUTOSOURCER") {
    return {
      data: undefined,
      loading: false,
      error: undefined,
    };
  }

  // Extract company name and job title from the viewjob model for filtering
  const jobCompanyName = viewjobOneGraphModel?.employer?.companyName;
  const jobTitle = viewjobOneGraphModel?.job?.jobTitle;

  if (!isViewjobOneGraphActive) {
    return {
      data: viewjobOneGraphModel?.employerOutreachEngagement?.engagements
        ? { engagements: viewjobOneGraphModel?.employerOutreachEngagement?.engagements }
        : undefined,
      loading: false,
      error: undefined,
    };
  }

  // When OneGraph is active (rn_viewjob_onegraph_tst grp 2), apply local filtering
  // to the GraphQL query results by company name and job title
  const transformedData = resumeEngagementDataBuilder(
    jobKey,
    resumeEngagementContext?.resumeEngagementData,
    jobCompanyName,
    jobTitle,
  );

  return {
    data: transformedData,
    loading: resumeEngagementContext?.resumeEngagementLoading ?? false,
    error: resumeEngagementContext?.resumeEngagementError,
  };
};
